import { useMemo, useState } from "react"
import { RiCalendarEventLine } from "@remixicon/react"
import { addDays, format, isToday, startOfWeek } from "date-fns"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@workspace/web-ui/components/accordion"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet"

import { 
  type CalendarEvent, 
  getAgendaEventsForDay 
} from "~/features/dashboard/pages/calendar/components"
import { cn } from "@workspace/web-ui/lib/utils"
import { Badge } from "@workspace/web-ui/components/badge"
import { isFuture, isPast } from "date-fns"
import RefillCardModal from "../pages/refills/components/refill-card"
import { useParams } from "@tanstack/react-router"
// import { EncounterDetailsContent } from "../../encounters/components/encounter-details"
import { AppointmentDetailsFromData } from "./appointment-details-content"
import { AppointmentDetailsModal } from "./event-modal"

interface ModWeekViewProps {
  currentDate: Date
  events: CalendarEvent[]
  onEventSelect: (event: CalendarEvent) => void
}

const formatTimeRange = (start: Date, end: Date) => {
  return `${format(start, "h:mm a")} - ${format(end, "h:mm a")}`
}

const getEventColor = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) 
      ? "border border-[#019939] text-green-700" 
      : isPast(event.start) 
        ? "bg-[#E1FAEA] border border-green-200 text-green-800" 
        : "bg-[#019939] text-white"
  } else {
    return isFuture(event.end)
      ? "border border-[#3B82F6] text-blue-700"
      : isPast(event.start)
        ? "bg-[#EFF6FF] border border-blue-200 text-blue-800"
        : "bg-[#3B82F6] text-white"
  }
}

const careEventColor = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) 
      ? "border border-[#019939] text-green-700" 
      : isPast(event.start) 
        ? "bg-[#E1FAEA] border-l-8 border-[#4272DD] text-green-800" 
        : "bg-[#019939] text-white"
  } else {
    return isFuture(event.end)
      ? "border border-[#3B82F6] text-blue-700"
      : isPast(event.start)
        ? "bg-[#EFF6FF] border-l-8 border-[#00C781] text-blue-800"
        : "bg-[#3B82F6] text-white"
  }
}

const getStatusBadgeStyles = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) 
      ? "text-[#202020CC] bg-gray-300 border border-gray-300 hover:bg-gray-300" 
      : isPast(event.start) 
        ? "text-[#14AD4D] hover:bg-transparent bg-transparent border border-[#14AD4D]"
        : "bg-[#019939] hover:bg-[#019939] text-white"
  } else {
    return isFuture(event.end)
      ? "text-[#202020CC] bg-gray-300 border border-gray-300 hover:bg-gray-300"
      : isPast(event.start)
        ? "text-blue-600 hover:bg-transparent bg-transparent border border-blue-600"
        : "bg-blue-600 hover:bg-blue-600 text-white"
  }
}

const getEventStatus = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return isFuture(event.end) ? "upcoming" : isPast(event.start) ? "completed" : "ongoing"
  } else {
    if (event.status) return event.status
    return isFuture(event.end) ? "pending" : isPast(event.start) ? "completed" : "processing"
  }
}

const EventCard = ({ event, onClick, program }: { event: CalendarEvent, onClick: () => void, program: string }) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        "p-4 rounded-md flex flex-col cursor-pointer",
        program === "urgent-care" ? careEventColor(event) : getEventColor(event)
      )}
    >
      {event.type === 'appointment' ? (
        <>
          <div className="flex items-center space-x-3 mb-1 w-full max-w-[800px]">
            <p className="font-medium">{event.clientName} Weight loss consulation</p>
            <Badge 
              className={cn(
                "rounded-full px-2 text-xs font-medium capitalize",
                getStatusBadgeStyles(event)
              )}
            >
              {getEventStatus(event)}
            </Badge>
          </div>
          <p className="text-sm">{formatTimeRange(event.start, event.end)}</p>
        </>
      ) : (
        <>
          <div className="flex items-center space-x-3 mb-1">
            <p className="font-medium">Refill: {event.medication}</p>
            <Badge 
              className={cn(
                "rounded-full px-2 text-xs font-medium capitalize",
                getStatusBadgeStyles(event)
              )}
            >
              {getEventStatus(event)}
            </Badge>
          </div>
          <p className="text-sm">{formatTimeRange(event.start, event.end)}</p>
          <p className="text-sm mt-1">Patient: {event.clientName}</p>
          <p className="text-sm">Quantity: {event.quantity}</p>
        </>
      )}
    </div>
  )
}

export function ModWeekView({ currentDate, events }: ModWeekViewProps) {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)

  // Show the entire week containing the provided date (Monday to Sunday)
  const days = useMemo(() => {
    // Find the start of the week (Monday) for the given date
    const weekStart = startOfWeek(new Date(currentDate), { weekStartsOn: 1 })
    
    // Return array of 7 days starting from Monday
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  }, [currentDate])

  // Check if there are any events at all
  const hasAnyEvents = events.length > 0

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event)
  }

  const handleSheetClose = () => {
    setSelectedEvent(null)
  }

  const { program = "" } = useParams({ strict: false});

  return (
    <>
      <div className="border-border/70 border-t px-4">
        {!hasAnyEvents ? (
          <div className="flex min-h-[70svh] flex-col items-center justify-center py-16 text-center">
            <RiCalendarEventLine size={32} className="text-muted-foreground/50 mb-2" />
            <h3 className="text-lg font-medium">
              {events[0]?.type === 'refill' ? 'No refills' : events[0]?.type === 'appointment' ? 'No appointments' : 'No events'}
            </h3>
          </div>
        ) : (
          days.map((day) => {
            const dayEvents = getAgendaEventsForDay(events, day)
            
            return (
              <div key={day.toString()} className="border-border/70 divide-y-2 shadow rounded-sm relative my-5 border">
                <Accordion type="single" collapsible defaultValue={`day-${day.toISOString()}`} className="w-full">
                  <AccordionItem value={`day-${day.toISOString()}`} className="border-none divide-y-2">
                    <AccordionTrigger className="flex w-full items-center justify-between p-4 hover:no-underline">
                      <span
                        className="flex h-6 items-center pe-4 text-[10px] uppercase data-today:font-medium sm:pe-4 sm:text-xs"
                        data-today={isToday(day) || undefined}
                      >
                        {format(day, "d MMM, EEEE")}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 p-4">
                        {dayEvents.length > 0 ? (
                          dayEvents.map((event) => (
                            <EventCard
                              key={event.id}
                              event={event}
                              program={program}
                              onClick={() => handleEventClick(event)}
                            />
                          ))
                        ) : (
                          <div className="py-2 w-full flex items-center justify-center text-[#2D2F35] text-sm">No appointments</div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )
          })
        )}
      </div>

      {/* Sheet for displaying event details */}
      <Sheet open={selectedEvent !== null} onOpenChange={(open) => !open && handleSheetClose()}>
        <SheetContent className="h-screen sm:max-w-[600px] w-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <SheetHeader>
            <SheetTitle>
              {selectedEvent && (
                selectedEvent.type === 'appointment' 
                  ? `${selectedEvent.clientName}'s Appointment` 
                  : `Refill: ${selectedEvent.medication}`
              )}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            {selectedEvent && (
              selectedEvent.type === 'appointment' ? (
                program === "urgent-care" ? (
                  <AppointmentDetailsFromData appointment={selectedEvent} />
                ) : (
                  <AppointmentDetailsModal
                    onClose={handleSheetClose}
                    data={selectedEvent}
                  />
                )
              ) : (
                <RefillCardModal
                  appointment={selectedEvent}
                  isSelected={true}
                  onOpen={() => {}}
                  onClose={handleSheetClose}
                />
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}